import React, { useState, useMemo } from 'react';

import Autocomplete, { type AutocompleteProps } from 'src/components/input/autocomplete/Autocomplete';
import { type ISnippet, useSnippets } from 'src/services/api';
import type { Option } from 'src/components/input/select/Select';

type Props = Omit<AutocompleteProps<string | undefined>, 'options' | 'onChange'> & {
    getOptionDisabled?: (option: Option<string | undefined>) => boolean,
    onChange: (snippet?: ISnippet) => void,
}

export default function SelectSnippet({
    value,
    getOptionDisabled,
    ...props
}: Props) {
    const { data: snippets } = useSnippets();

    const [_value, setValue] = useState<string | undefined>(value);

    const onChange = (newValue?: string) => {
        setValue(newValue);
        const snippet = snippets?.find((item) => item.contents === newValue);
        props.onChange(snippet);
    };

    const options = useMemo(() => {
        if (!snippets) {
            return [];
        }

        const _options = snippets.map((snippet) => ({
            value: snippet.contents,
            label: snippet.name,
        }));

        return _options;
    }, [snippets]);

    return (
        <Autocomplete
            {...props}
            onChange={onChange}
            value={_value}
            options={options}
            getOptionDisabled={getOptionDisabled}
            data-testid="snippet-select"
        />
    );
}
